import { supabase } from './supabase';
import { evaluateSlots } from './slotEngine';
import { occupiedMinutes } from './slotOccupancy';

export function isoWeekday(day) {
  return day.getDay() === 0 ? 7 : day.getDay();
}

export function dateInputValue(value = new Date()) {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseDateInput(value) {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

async function fetchBusyWindows(branchId, day, intervalMinutes) {
  const start = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const end = new Date(start);
  end.setDate(end.getDate() + 1);

  const { data, error } = await supabase
    .from('bookings')
    .select(
      'id, vehicle_id, scheduled_at, estimated_minutes, preferred_mechanic_id, status, services:service_id(estimated_minutes)'
    )
    .eq('branch_id', branchId)
    .gte('scheduled_at', start.toISOString())
    .lt('scheduled_at', end.toISOString())
    .not('status', 'in', '(cancelled,completed)');

  if (error) throw error;

  return (data || []).map((row) => {
    const startAt = new Date(row.scheduled_at);
    const serviceMinutes = row.services?.estimated_minutes;
    const work = row.estimated_minutes ?? serviceMinutes ?? 60;
    return {
      bookingId: row.id,
      vehicleId: row.vehicle_id,
      mechanicId: row.preferred_mechanic_id,
      start: startAt,
      minutes: occupiedMinutes(Number(work) || 60, intervalMinutes),
    };
  });
}

export async function fetchBookingSlotOptions({
  branchId,
  day,
  durationMinutes,
  mechanicId,
  vehicleId,
  ignoreBookingId,
}) {
  const [{ data: branch, error: branchError }, { data: hourRows, error: hoursError }] =
    await Promise.all([
      supabase
        .from('branches')
        .select('id, slot_minutes, parallel_slots')
        .eq('id', branchId)
        .maybeSingle(),
      supabase
        .from('branch_hours')
        .select('weekday, opens_at, closes_at, is_closed')
        .eq('branch_id', branchId),
    ]);

  if (branchError) throw branchError;
  if (hoursError) throw hoursError;
  if (!branch) throw new Error('Branch not found.');

  const intervalMinutes = Number(branch.slot_minutes) || 30;
  const parallelSlots = Math.min(2, Math.max(1, Number(branch.parallel_slots) || 1));
  const hours = (hourRows || []).find((row) => row.weekday === isoWeekday(day));
  const busy = await fetchBusyWindows(branchId, day, intervalMinutes);

  return evaluateSlots({
    day,
    hours,
    intervalMinutes,
    durationMinutes,
    parallelSlots,
    busy,
    mechanicId,
    vehicleId,
    ignoreBookingId,
  });
}

export async function updateBookingSchedule(id, scheduledAt) {
  const { data, error } = await supabase
    .from('bookings')
    .update({ scheduled_at: scheduledAt })
    .eq('id', id)
    .select('id, scheduled_at')
    .maybeSingle();
  if (error) return { error };
  if (!data) {
    return {
      error: {
        message: 'Visit time was not saved. Your account cannot update this booking.',
      },
    };
  }
  return { data };
}
